import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, FileTextIcon, RotateCcwIcon } from "lucide-react";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import {
    SessionCoachingPanel,
    type SessionCoachingPanelHandle,
} from "@component/training/session-coaching-panel";
import {
    RecordingPlayer,
    type RecordingPlayerHandle,
} from "@component/training/recording-player";
import { AttemptHistory } from "@component/training/attempt-history";
import HighlightTranscript from "@component/highlight-transcript";
import {
    getSessionResults,
    getSessionHistory,
    retrySession,
    getSessionAudioUrl,
    apiPollResults,
} from "@service/training";
import { transcriptToHighlights } from "@util/training-feedback-adapter";
import type {
    TrainingSession,
    SessionAttempt,
    RubricCriterion,
} from "@type/training";
import { cn } from "@util/tailwind";

const PREVIEW_SESSION: TrainingSession = {
    id: "preview",
    campaign_id: "preview",
    employee_email: "demo@ourteam.ai",
    employee_name: "Demo User",
    employee_role: "Counter Officer",
    employee_department: "Branch Operations",
    token: "preview",
    attempt_number: 2,
    status: "completed",
    started_at: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    completed_at: new Date().toISOString(),
    rubric_scores: {
        empathy: 4,
        problem_resolution: 3,
        product_knowledge: 4,
        tone: 4,
        call_handling: 3,
    },
    total_score: 18,
    qualitative_feedback:
        "You handled the customer's frustration well and showed strong empathy. Your resolution was clear, though you could have confirmed next steps more explicitly before closing the call.",
    feedback_strengths:
        "You acknowledged the customer's frustration early and stayed calm throughout. Your product knowledge was solid.",
    feedback_growth:
        "Try confirming the resolution explicitly before ending the call — a simple 'Does that work for you?' goes a long way.",
    is_test: false,
    campaign_name: "Customer Service Basics",
    persona_name: "Sarah Chen",
    // Demo manifest so ?preview=true shows the "Knowledge used in this
    // evaluation" card exactly as it will render once the BE emits it
    // (ourteam-backend#3580 Phase 0). Includes one truncated doc and one
    // budget-excluded doc so both warning states are visible.
    knowledge_manifest: {
        documents: [
            {
                document_id: "preview-kb-1",
                name: "Product Catalogue Q3 2026.pdf",
                scope: "workspace",
                included: true,
                truncated: false,
                chars_included: 5200,
            },
            {
                document_id: "preview-kb-2",
                name: "Refund & Escalation Policy.pdf",
                scope: "workspace",
                included: true,
                truncated: false,
                chars_included: 3100,
            },
            {
                document_id: "preview-kb-3",
                name: "CS Basics Call Script.pdf",
                scope: "campaign",
                included: true,
                truncated: true,
                chars_included: 3700,
            },
            {
                document_id: "preview-kb-4",
                name: "Legacy Onboarding Guide.pdf",
                scope: "workspace",
                included: false,
                truncated: false,
                chars_included: 0,
            },
        ],
        context_chars_used: 12000,
        context_chars_budget: 12000,
        generated_at: "2026-07-16T09:30:00Z",
    },
};

const PREVIEW_RUBRIC: RubricCriterion[] = [
    {
        id: "1",
        name: "Empathy",
        weight: 1,
        description: "Acknowledges and validates the customer's feelings.",
        correct_answer:
            "Agent names the emotion and affirms it before moving to resolution.",
        scoring_guide: "5 = proactive empathy, 1 = ignored feelings entirely.",
    },
    {
        id: "2",
        name: "Problem Resolution",
        weight: 1,
        description: "Reaches a clear, actionable resolution.",
        correct_answer:
            "Agent commits to a specific next step with a timeline.",
        scoring_guide: "5 = clear resolution confirmed, 1 = call ended without resolution.",
    },
    {
        id: "3",
        name: "Product Knowledge",
        weight: 1,
        description: "Demonstrates accurate knowledge of policies and processes.",
        correct_answer:
            "Agent answers product questions correctly and confidently.",
        scoring_guide: "5 = fully accurate, 1 = multiple errors.",
    },
    {
        id: "4",
        name: "Tone",
        weight: 1,
        description: "Maintains a calm, professional tone throughout.",
        correct_answer: "Agent stays composed even when customer escalates.",
        scoring_guide:
            "5 = consistently warm and professional, 1 = defensive or dismissive.",
    },
    {
        id: "5",
        name: "Call Handling",
        weight: 1,
        description: "Structures the call with a clear opener, body, and close.",
        correct_answer:
            "Agent opens, discovers, resolves, and closes with a summary.",
        scoring_guide: "5 = well-structured call, 1 = unstructured and disorganised.",
    },
];

const PREVIEW_ATTEMPTS: SessionAttempt[] = [
    {
        attempt_number: 1,
        status: "completed",
        total_score: 14,
        completed_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        token: "preview-attempt-1",
    },
    {
        attempt_number: 2,
        status: "completed",
        total_score: 18,
        completed_at: new Date().toISOString(),
        token: "preview",
    },
];


function formatDateGB(dateStr: string | null | undefined): string {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function TrainingResultsPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const isPreview = params.get("preview") === "true";
    const modeIsEmployer = params.get("mode") === "employer";

    const [session, setSession] = useState<TrainingSession | null>(null);
    const [rubricCriteria, setRubricCriteria] = useState<RubricCriterion[]>([]);
    const [attempts, setAttempts] = useState<SessionAttempt[]>([]);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [retryLoading, setRetryLoading] = useState(false);
    const [retryError, setRetryError] = useState<string | null>(null);

    const recordingPlayerRef = useRef<RecordingPlayerHandle>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const coachingPanelRef = useRef<SessionCoachingPanelHandle>(null);

    useEffect(() => {
        if (isPreview) {
            setSession(PREVIEW_SESSION);
            setRubricCriteria(PREVIEW_RUBRIC);
            setAttempts(PREVIEW_ATTEMPTS);
            setLoading(false);
            return;
        }

        if (!token) return;

        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const result = await getSessionResults(token!);
                if (cancelled) return;

                if (!result) {
                    setSession(null);
                    setLoading(false);
                    return;
                }

                // An "invited" attempt was never started — there's nothing to
                // score or show here, so send the user to the session page to
                // begin the roleplay instead of rendering a stuck results view.
                if (result.session.status === "invited") {
                    navigate(`/training-session/${token}`, { replace: true });
                    return;
                }

                setSession(result.session);
                // rubric_criteria comes from the results response — no separate getRubric() needed
                // (a separate call would fail for employees who have no JWT).
                setRubricCriteria(result.rubric_criteria);

                const history = await getSessionHistory(token!);

                if (cancelled) return;
                setAttempts(history);
                if (!result.session.recording_url) setAudioUrl(getSessionAudioUrl(token!));
            } catch {
                if (!cancelled) setSession(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [token, isPreview]);

    // Poll for scoring completion when the session arrives before the BE finishes
    // (onDisconnect navigates here immediately; background thread may still be running).
    useEffect(() => {
        if (!session || session.status === "completed" || session.status === "failed" || isPreview) return;
        if (!token) return;

        let cancelled = false;
        const DELAYS_MS = [2000, 4000, 6000, 8000, 8000, 8000, 8000, 8000, 8000, 8000];
        const BUDGET_MS = 90_000;
        const startedAt = Date.now();
        let attempt = 0;

        async function poll() {
            while (!cancelled && Date.now() - startedAt < BUDGET_MS) {
                await new Promise((r) =>
                    setTimeout(r, DELAYS_MS[Math.min(attempt, DELAYS_MS.length - 1)])
                );
                attempt++;
                if (cancelled) return;
                try {
                    const outcome = await apiPollResults(token!);
                    if (outcome.kind === "ready") {
                        if (cancelled) return;
                        // Refresh the attempt history BEFORE flipping
                        // session.status to "completed": setSession changes this
                        // effect's `session?.status` dependency, which re-runs the
                        // effect and flips `cancelled` to true — so a setAttempts
                        // after that point would be silently skipped by the
                        // `!cancelled` guard (the /history call fires but the
                        // sidebar never updates, leaving the new attempt "started").
                        const history = await getSessionHistory(token!);
                        if (cancelled) return;
                        if (history.length > 0) setAttempts(history);
                        setSession(outcome.session);
                        return;
                    }
                    if (outcome.kind === "failed") {
                        // Update session to failed so the "Analyzing" banner
                        // stops spinning instead of running until budget expires.
                        if (!cancelled) setSession((prev) => prev ? { ...prev, status: "failed" as const } : null);
                        return;
                    }
                } catch {
                    // network blip — keep polling
                }
            }
        }

        poll();
        return () => { cancelled = true; };
    }, [session?.status, token, isPreview]);

    const handleRetry = async () => {
        setRetryError(null);
        if (!token) return;
        setRetryLoading(true);
        try {
            // If an attempt is still active (not completed), resume it instead of
            // minting a duplicate. Check fresh history so we catch a run that was
            // started after this page loaded; pick the latest such attempt.
            const history = await getSessionHistory(token);
            const pending = history
                .filter((a) => a.status !== "completed" && a.token)
                .reduce<SessionAttempt | null>(
                    (latest, a) =>
                        !latest || a.attempt_number > latest.attempt_number ? a : latest,
                    null,
                );
            if (pending?.token) {
                navigate(`/training-session/${pending.token}`);
                return;
            }

            const newSession = await retrySession(token);
            if (newSession) {
                navigate(`/training-session/${newSession.token}`);
            } else {
                setRetryError(
                    "Couldn't start a new attempt. Please try again or contact your HR team.",
                );
            }
        } catch {
            setRetryError("Something went wrong. Please try again.");
        } finally {
            setRetryLoading(false);
        }
    };

    const handlePlayFromTimestamp = (timestamp: string) => {
        recordingPlayerRef.current?.playFrom(timestamp);
        // Jump the right panel to the tab (Issue Resolution / Communication /
        // Delivery) that owns the feedback item at this timestamp, so clicking
        // a timeline dot lands the user on the matching coaching tab.
        coachingPanelRef.current?.focusTimestamp(timestamp);
        playerContainerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900" />
            </div>
        );
    }

    if (!session) {
        return (
            <div className="flex min-h-screen items-center justify-center p-6">
                <Card className="max-w-md">
                    <CardContent className="space-y-4 p-6 text-center">
                        <p className="text-sm text-gray-600">Results not available.</p>
                        <Button variant="outline" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // mode=employer: explicit employer view; is_test: employer testing as employee
    const isEmployer = modeIsEmployer || session.is_test === true;

    const handleViewAttempt = (attemptToken: string) => {
        navigate(
            `/training-results/${attemptToken}${isEmployer ? "?mode=employer" : ""}`,
        );
    };

    // Headline score is the BE-provided 0-100 `total_score`, NOT the sum of the
    // per-criteria rubric values (those are /5 each, summing to ≤25 — a
    // different scale). The rubric chips below still show each criterion out of 5.
    const score = session.total_score ?? 0;
    const hasScore = session.total_score !== null && session.total_score !== undefined;
    const attemptNumber =
        typeof session.attempt_number === "number" && session.attempt_number > 0
            ? session.attempt_number
            : 1;
    const highlights = transcriptToHighlights(session.transcript_full ?? session.transcript);
    const retakesAvailable = session.status === "completed";

    const backUrl = session.campaign_id
        ? `/training/campaigns/${session.campaign_id}?openEmployee=${encodeURIComponent(session.employee_email)}`
        : "/training/campaigns";

    const hasSidebar = isEmployer ? attempts.length >= 1 : attempts.length > 1;

    const isScoring = session.status !== "completed" && session.status !== "failed";

    // The sidebar reads `attempts` from getSessionHistory, which can lag behind
    // the live `session` for the attempt currently being viewed (e.g. the score
    // is already on `session` while history still has total_score: null mid-
    // analyzing). Overlay the live session onto its matching entry so the
    // sidebar shows the freshest score/status instead of a stale "—". Prefer a
    // non-null score from either source so a re-score never wipes a known score.
    const sidebarAttempts = attempts.map((a) =>
        a.token === token
            ? {
                  ...a,
                  status: session.status,
                  total_score: session.total_score ?? a.total_score,
                  completed_at: session.completed_at ?? a.completed_at,
              }
            : a,
    );

    return (
        // Desktop (lg+) is a fixed viewport shell: the page itself never scrolls;
        // the top bar, the Attempts sidebar, and each panel are their own scroll
        // regions. Below lg the columns stack and the page scrolls normally.
        <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-white flex flex-col">
            {isScoring && (
                <div className="bg-blue-50 border-b border-blue-100 text-blue-700 text-xs font-medium text-center py-2 px-4 flex items-center justify-center gap-2">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
                    Analyzing your performance, the page will auto-refresh and results will appear shortly.
                </div>
            )}
            {/* Sticky top bar */}
            <div className="h-12 sticky top-0 z-40 bg-white border-b border-gray-200 flex items-center px-6 gap-3 flex-shrink-0">
                {isEmployer && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-gray-600"
                        onClick={() => navigate(backUrl)}
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Back to dashboard
                    </Button>
                )}
                {!isEmployer && retakesAvailable && (
                    <Button
                        size="sm"
                        className="ml-auto gap-2"
                        onClick={handleRetry}
                        disabled={retryLoading}
                    >
                        <RotateCcwIcon className="h-4 w-4" />
                        {retryLoading ? "Starting..." : "Practice Again"}
                    </Button>
                )}
            </div>

            {/* Body */}
            <div className="flex flex-1 min-h-0">
                {/* Sticky left sidebar */}
                {hasSidebar && (
                    <div className="w-52 flex-shrink-0 sticky top-12 h-[calc(100vh-3rem)] overflow-y-auto border-r border-gray-100 py-4">
                        <div className="px-4 mb-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                            Attempts
                        </div>
                        <AttemptHistory
                            attempts={sidebarAttempts}
                            currentToken={token}
                            onViewAttempt={handleViewAttempt}
                            variant="sidebar"
                            isEmployer={isEmployer}
                        />
                    </div>
                )}

                {/* Main content */}
                <div className="flex-1 min-w-0 px-10 py-10 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:flex-1 lg:min-h-0">
                        {/* Left panel — its own scroll region on lg (recording +
                            transcript). The info header and recording player stay
                            put; only the transcript scrolls. */}
                        <div className="space-y-6 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden">
                            {/* Info header */}
                            <div className="flex items-start justify-between gap-4 lg:flex-shrink-0">
                                <div className="min-w-0">
                                    {isEmployer ? (
                                        <>
                                            <div className="font-semibold text-gray-900 mb-1">
                                                {session.employee_name}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
                                                {[
                                                    session.employee_role,
                                                    session.employee_department,
                                                    session.persona_name,
                                                    session.campaign_name,
                                                    session.completed_at
                                                        ? formatDateGB(session.completed_at)
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .map((detail, i, arr) => (
                                                        <span key={i} className="flex items-center gap-1">
                                                            {detail}
                                                            {i < arr.length - 1 && (
                                                                <span className="text-gray-300">·</span>
                                                            )}
                                                        </span>
                                                    ))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">
                                                Attempt {attemptNumber}
                                                {session.campaign_name
                                                    ? ` · ${session.campaign_name}`
                                                    : ""}
                                            </div>
                                            {session.completed_at && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                    Completed on {formatDateGB(session.completed_at)}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Score on right */}
                                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                    {hasScore ? (
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-gray-900 tabular-nums leading-none">
                                                {score}
                                            </span>
                                            <span className="text-base text-gray-400">
                                                /100
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="inline-block px-3 py-1 border rounded-full text-xs font-semibold text-gray-600 bg-gray-50 border-gray-200">
                                            Scoring in progress
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Recording player. `overflow-hidden` removed
                                because it clipped the feedback-dot tooltips
                                that float above the scrub track; the inner
                                component already provides its own rounded
                                border, so the wrapper's rounding was redundant. */}
                            <div ref={playerContainerRef} className="rounded-md lg:flex-shrink-0">
                                <RecordingPlayer
                                    ref={recordingPlayerRef}
                                    recordingUrl={session.recording_url ?? audioUrl}
                                    feedbackItems={session.feedback_items ?? []}
                                    onTimestampClick={handlePlayFromTimestamp}
                                    isProcessing={isScoring}
                                />
                            </div>

                            {/* Transcript — fills the rest of the left panel and
                                scrolls on its own; its header stays fixed. */}
                            <div className="rounded-md overflow-hidden lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
                                <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-2 lg:flex-shrink-0">
                                    <FileTextIcon className="h-4 w-4 text-blue-600" />
                                    <h3 className="font-medium text-gray-800">Transcript</h3>
                                    {highlights.length > 0 && (
                                        <span className="ml-auto bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                            {highlights.length}
                                        </span>
                                    )}
                                </div>
                                {/* Below lg: fixed cap so the stacked page scrolls
                                    normally. On lg+: the transcript flexes to fill the
                                    left panel and scrolls within it. */}
                                <div className="p-4 max-h-[480px] overflow-y-auto lg:max-h-none lg:flex-1 lg:min-h-0">
                                    {isScoring && highlights.length === 0 ? (
                                        <TranscriptSkeleton />
                                    ) : highlights.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                            <div className="bg-gray-100 p-4 rounded-full mb-4">
                                                <FileTextIcon className="h-8 w-8 text-gray-400" />
                                            </div>
                                            <h3 className="text-sm font-medium text-gray-900 mb-2">
                                                Transcript not ready yet.
                                            </h3>
                                            <p className="text-xs text-gray-500 max-w-xs">
                                                Your roleplay transcript will appear here once the
                                                scoring pipeline finishes processing.
                                            </p>
                                        </div>
                                    ) : (
                                        (() => {
                                            let questionCounter = 0;
                                            const candidateName =
                                                session.employee_name?.trim() || undefined;
                                            const interviewerName =
                                                session.persona_name?.trim() || undefined;
                                            return highlights.map((segment, index) => {
                                                const hasQ =
                                                    (segment.question ?? "").trim().length > 0;
                                                const questionNumber = hasQ
                                                    ? ++questionCounter
                                                    : undefined;
                                                return (
                                                    <HighlightTranscript
                                                        key={index}
                                                        time={segment.time}
                                                        question={segment.question}
                                                        answer={segment.answer}
                                                        questionNumber={questionNumber}
                                                        candidateName={candidateName}
                                                        interviewerName={interviewerName}
                                                        isActive={index === 0}
                                                    />
                                                );
                                            });
                                        })()
                                    )}
                                </div>
                            </div>

                        </div>

                        {/* Right panel — its own scroll region on lg. The insights
                            banner and tab bar stay fixed; only the tab content
                            scrolls (handled inside SessionCoachingPanel). */}
                        <div className="lg:min-h-0 lg:overflow-hidden">
                            <SessionCoachingPanel
                                ref={coachingPanelRef}
                                session={session}
                                rubricCriteria={rubricCriteria}
                                isEmployer={isEmployer}
                            />
                        </div>
                    </div>

                    {retryError && (
                        <p className="text-sm text-red-600 mt-4 text-center lg:flex-shrink-0">{retryError}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

// Skeleton that mimics the highlight transcript chat layout — alternating
// left/right "bubbles" of pulsing gray blocks. Shown only while the session
// is still being scored so users see the page is actively loading rather
// than an empty-state freeze.
function TranscriptSkeleton() {
    const rows = [
        { side: "left",  widths: ["w-3/5", "w-2/5"] },
        { side: "right", widths: ["w-1/2"] },
        { side: "left",  widths: ["w-4/5", "w-3/5", "w-2/5"] },
        { side: "right", widths: ["w-2/3"] },
    ] as const;
    return (
        <div className="space-y-5 py-2" aria-busy="true" aria-live="polite">
            {rows.map((row, idx) => (
                <div
                    key={idx}
                    className={cn(
                        "flex",
                        row.side === "right" ? "justify-end" : "justify-start",
                    )}
                >
                    <div className={cn("max-w-[70%] space-y-1.5", row.side === "right" && "items-end")}>
                        <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
                        {row.widths.map((w, j) => (
                            <div
                                key={j}
                                className={cn(
                                    "h-3 rounded bg-gray-200 animate-pulse",
                                    w,
                                )}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
